import React, { Fragment } from 'react';
import merge from 'lodash/merge';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { DATA_SOURCE_TYPE, VITALS_ACTIVITY_TYPE } from '../../constants';
import { PERIOD, PROVIDER_OPTION } from '../../../common/Charts/constants';
import InfoWrapper from '../../../common/Charts/InfoWrapper';
import { mapChartData, dataConverter } from '../../../common/Charts/helpers';

import ModalWrapper from '../../../common/Charts/ModalWrapper';
import ChartWrapper from '../../../common/Charts/ChartWrapper';
import ChartProvider from '../../../common/Charts/ChartProvider';
import HistoryWrapper from '../../../common/Charts/HistoryWrapper';
import { DEFAULTS, TIMELINE } from '../../../common/Charts/options';
import { TOOLTIP_VERTICAL_LINE } from '../../../common/Charts/plugins';
import * as bodyTemperatureMeasurement from '../../../../redux/profiles/patientHealthboard/body-temperature-measurements-req';
import * as bodyTemperatureMeasurementModal from '../../../../redux/profiles/patientHealthboard/body-temperature-measurements-modal-req';
import {
  useChartActions,
  useChartFormats,
  useHistoryActions,
  useSafeDataSourceTitle,
} from '../../../common/Charts/hooks';
import {
  selectHistoricMeasurementsList,
  selectHistoricMeasurementsListPending,
  selectBodyTemperatureMeasurementEntity,
  selectBodyTemperatureMeasurementModalEntity,
  selectBodyTemperatureMeasurementEntityPending,
  selectBodyTemperatureMeasurementModalEntityPending,
} from '../../../../redux/profiles/patientHealthboard/selectors';
import {
  calculateMinMaxRangeForRawValue,
  convertCelsiusToFahrenheit,
  getOriginalProviderCode,
  roundPrecision,
} from '../../utils';
import { getCustomOptions } from './options';
import { useCalculatePaddingYAxis } from '../useCalculatePaddingYAxis';

const { DAY, WEEK, MONTH } = PERIOD;
const { BODY_TEMPERATURE } = VITALS_ACTIVITY_TYPE;
const { NAME } = PROVIDER_OPTION;

const BodyTemperatureChart = ({
  intl: { formatMessage },
  isTemperatureInCelsius,
}) => {
  const historyData = useSelector(selectHistoricMeasurementsList);
  const periodDataTemperature = useSelector(
    selectBodyTemperatureMeasurementEntity,
  );
  const modalDataTemperature = useSelector(
    selectBodyTemperatureMeasurementModalEntity,
  );
  const historyLoadingTemperature = useSelector(
    selectHistoricMeasurementsListPending,
  );
  const periodLoadingTemperature = useSelector(
    selectBodyTemperatureMeasurementEntityPending,
  );
  const modalLoadingTemperature = useSelector(
    selectBodyTemperatureMeasurementModalEntityPending,
  );

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const formats = useChartFormats({ formatMessage });

  const { loadHistory } = useHistoryActions({ type: BODY_TEMPERATURE });

  const { isModal, openDay, closeDay, baseDate, loadPeriod } = useChartActions({
    actions: bodyTemperatureMeasurement.actions,
    modalActions: bodyTemperatureMeasurementModal.actions,
  });

  const metric = isTemperatureInCelsius
    ? formatMessage({
        id: 'patient.healthboard.body_temperature.metric.celsius',
      })
    : formatMessage({
        id: 'patient.healthboard.body_temperature.metric.fahrenheit',
      });

  const paddingYAxisTicks = useCalculatePaddingYAxis();

  const setConfig = (data, date) => ({ period }) => {
    const { min, max } = calculateMinMaxRangeForRawValue(data);
    return {
      options: merge(DEFAULTS, TIMELINE[period], {
        layout: {
          padding: {
            top: isModal ? 120 : 140,
          },
        },
        tooltips: {
          enabled: [DAY, WEEK, MONTH].includes(period),
          callbacks: {
            ...DEFAULTS.tooltips,
            footer(points, { datasets, customOptions }) {
              const { providers } = customOptions;

              const { datasetIndex, yLabel } = points[0];
              const dataset = datasetIndex
                ? datasets[datasetIndex]
                : datasets[0];
              const providerCode = dataset.label.split('.')[0];
              if (providerCode === DATA_SOURCE_TYPE.DEVICES) {
                const modalDevicesPeriodData = modalDataTemperature.find(
                  el => el.provider.code === DATA_SOURCE_TYPE.DEVICES,
                );
                const tooltipData = (isModal &&
                modalDevicesPeriodData.measurements
                  ? modalDevicesPeriodData.measurements
                  : historyData
                ).find(el => el.value.baseValue === yLabel);

                if (tooltipData) {
                  return getSafeDataSourceTitle(
                    getOriginalProviderCode(tooltipData.originalProviderCodes),
                    formatMessage,
                  );
                }
              }
              return providers[providerCode][NAME];
            },
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                min: !isTemperatureInCelsius
                  ? convertCelsiusToFahrenheit(min)
                  : min,
                max: !isTemperatureInCelsius
                  ? convertCelsiusToFahrenheit(max)
                  : max,
                autoSkip: true,
                autoSkipPadding: paddingYAxisTicks,
                stepSize: 0.1,
                callback(value, _, allData) {
                  const roundedValue = roundPrecision(value, 1);
                  if (allData.includes(roundedValue) && roundedValue !== value)
                    return null;
                  return roundedValue;
                },
              },
            },
          ],
        },
      }),
      type: 'line',
      plugins: [TOOLTIP_VERTICAL_LINE],
      data: mapChartData(
        data,
        getCustomOptions({
          period,
          date,
          formats,
          metric,
          shouldConvertToFahrenheit: !isTemperatureInCelsius,
        }),
      ),
    };
  };

  const context = {
    isModal,
    mark: metric,
    converter: dataConverter.float,
    shouldConvertToFahrenheit: !isTemperatureInCelsius,
    range: formatMessage({ id: 'patient.healthboard.average' }),
  };

  return (
    <Fragment>
      <ChartProvider extend={context}>
        <ChartWrapper
          onLoad={loadPeriod}
          loading={periodLoadingTemperature}
          setConfig={setConfig(periodDataTemperature)}
        />

        <InfoWrapper
          subtitle={formatMessage({ id: 'patient.healthboard.averageData' })}
          title={formatMessage({
            id: 'patient.healthboard.titleHistoric.body_temperature',
          })}
        >
          <HistoryWrapper
            unit={DAY}
            onClick={openDay}
            data={historyData}
            onLoad={loadHistory}
            loading={historyLoadingTemperature}
          />
        </InfoWrapper>
      </ChartProvider>

      <ModalWrapper
        title={formatMessage({
          id: 'patient.healthboard.body_temperature',
        })}
        open={isModal}
        onClose={closeDay}
      >
        <ChartProvider extend={context}>
          <ChartWrapper
            isModal
            loading={modalLoadingTemperature}
            setConfig={setConfig(modalDataTemperature, baseDate)}
            footer={<HistoryWrapper isModal data={modalDataTemperature} />}
          />
        </ChartProvider>
      </ModalWrapper>
    </Fragment>
  );
};

BodyTemperatureChart.propTypes = {
  intl: PropTypes.object.isRequired,
  isTemperatureInCelsius: PropTypes.bool,
};

export default injectIntl(BodyTemperatureChart);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/TimeScalableChart/BodyTemperatureChart/index.jsx